export const REFUND_SERVICE_FEE_BPS = 500;
export const REFUND_BPS_DENOMINATOR = 10_000;
export const REFUND_PRICE_THRESHOLD_FEN = 1_880;
export const LOW_PRICE_REFUND_WINDOW_SECONDS = 14 * 86_400;
export const HIGH_PRICE_REFUND_WINDOW_SECONDS = 168 * 3_600;

export const REFUND_POLICY_CODES = {
  legacy: "LEGACY_PRORATED_5_PERCENT",
  lowPrice: "LOW_PRICE_14_DAY_HALF",
  highPrice: "HIGH_PRICE_SIGUAMAP_168_HOUR_USAGE",
  nonRefundable: "NON_REFUNDABLE",
} as const;

export type RefundPolicyCode =
  (typeof REFUND_POLICY_CODES)[keyof typeof REFUND_POLICY_CODES];

export function refundPolicyForAmountFen(amountFen: number): RefundPolicyCode {
  if (!Number.isSafeInteger(amountFen) || amountFen < 0) {
    throw new Error("Refund policy amount must be a non-negative safe integer");
  }
  return amountFen < REFUND_PRICE_THRESHOLD_FEN
    ? REFUND_POLICY_CODES.lowPrice
    : REFUND_POLICY_CODES.highPrice;
}

export function refundPolicyForProduct(
  productCode: string,
  amountFen: number,
): RefundPolicyCode {
  if (productCode === "super-bomber-trial-3d") {
    return REFUND_POLICY_CODES.nonRefundable;
  }
  return refundPolicyForAmountFen(amountFen);
}

export function calculateLowPriceRefundAmountFen(amountFen: number): number {
  if (!Number.isSafeInteger(amountFen) || amountFen < 0) {
    throw new Error("Refund amount must be a non-negative safe integer");
  }
  return Math.floor(amountFen / 2);
}

export function calculateSiguaMapRefundAmountFen(
  amountFen: number,
  qualifyingUsageCount: number,
): number {
  if (!Number.isSafeInteger(amountFen) || amountFen < 0) {
    throw new Error("Refund amount must be a non-negative safe integer");
  }
  if (
    !Number.isSafeInteger(qualifyingUsageCount) ||
    qualifyingUsageCount < 0
  ) {
    throw new Error("Qualifying usage count must be a non-negative safe integer");
  }
  const refundBps =
    [10_000, 8_500, 7_000, 5_500, 4_000][qualifyingUsageCount] ?? 0;
  return Math.floor((amountFen * refundBps) / REFUND_BPS_DENOMINATOR);
}

export interface DefaultRefundAmountInput {
  amountFen: number;
  durationSeconds: number;
  serviceEndsAt: Date;
  now?: Date | undefined;
  serviceFeeBps?: number | undefined;
}

/**
 * Calculates the default refund in fen without ever exceeding the paid amount.
 * The remaining service time is prorated first, then the service fee is kept.
 */
export function calculateDefaultRefundAmountFen(
  input: DefaultRefundAmountInput,
): number {
  if (!Number.isSafeInteger(input.amountFen) || input.amountFen < 0) {
    throw new Error("Refund amount must be a non-negative safe integer");
  }
  if (
    !Number.isSafeInteger(input.durationSeconds) ||
    input.durationSeconds <= 0
  ) {
    throw new Error("Refund duration must be a positive safe integer");
  }

  const serviceFeeBps = input.serviceFeeBps ?? REFUND_SERVICE_FEE_BPS;
  if (
    !Number.isSafeInteger(serviceFeeBps) ||
    serviceFeeBps < 0 ||
    serviceFeeBps > REFUND_BPS_DENOMINATOR
  ) {
    throw new Error("Refund service fee is outside the supported range");
  }

  const now = input.now ?? new Date();
  const remainingSeconds = Math.min(
    input.durationSeconds,
    Math.max(
      0,
      Math.ceil((input.serviceEndsAt.getTime() - now.getTime()) / 1_000),
    ),
  );
  const refundFen =
    (BigInt(input.amountFen) *
      BigInt(remainingSeconds) *
      BigInt(REFUND_BPS_DENOMINATOR - serviceFeeBps)) /
    (BigInt(input.durationSeconds) * BigInt(REFUND_BPS_DENOMINATOR));
  return Number(refundFen);
}
